import { z } from 'zod';
import { ReviewStatus, type ReviewToken } from '@sleed/bizcommerce-api';

/**
 * Review moderation / editorial review form. Customer comments edit only the moderated fields (status, rating, title,
 * text, visibility, featured, sort); contributor (editorial) reviews also carry the product, the contributor, its
 * category, the free-text reviewer + source and the review date (legacy ContributorProductReviews grid). Server-owned
 * fields not on the form (from/to entities, ML translations, ERP codes) survive via spread-on-update.
 */
export const reviewSchema = z.object({
	id: z.number().optional(),
	to_id: z.number({ message: 'validation.required' }).int().positive('validation.required'),
	from_id: z.number().int().nullable(),
	con_cat_id: z.number().int().nullable(),
	reviewer: z.string().max(300, 'validation.tooLong'),
	source: z.string().max(300, 'validation.tooLong'),
	review_date: z.string().nullable(),
	rating: z.number().min(0, 'validation.notNegative').max(5, 'validation.ratingMax').nullable(),
	tl: z.string().max(300, 'validation.tooLong'),
	review: z.string(),
	st: z.number().int(),
	iv: z.boolean(),
	isFeatured: z.boolean(),
	si: z.number({ message: 'validation.required' }).int()
});

export type ReviewFormValues = z.infer<typeof reviewSchema>;

/** A new editorial review: accepted and visible (it is the shop's own content), today's date. */
export function emptyEditorialReview(now: Date = new Date()): ReviewFormValues {
	return {
		to_id: 0,
		from_id: null,
		con_cat_id: null,
		reviewer: '',
		source: '',
		review_date: now.toISOString(),
		rating: null,
		tl: '',
		review: '',
		st: ReviewStatus.Accepted,
		iv: true,
		isFeatured: false,
		si: 0
	};
}

export function tokenToForm(token: ReviewToken): ReviewFormValues {
	return {
		id: token.id,
		to_id: token.to_id ?? 0,
		from_id: token.from_id ?? null,
		con_cat_id: token.con_cat_id ?? null,
		reviewer: token.reviewer ?? '',
		source: token.source ?? '',
		review_date: token.review_date ? String(token.review_date) : null,
		rating: token.rating ?? null,
		tl: token.tl ?? '',
		review: token.review ?? '',
		st: token.st ?? ReviewStatus.Pending,
		iv: token.iv ?? false,
		isFeatured: token.isFeatured ?? false,
		si: token.si ?? 0
	};
}

/**
 * The token to save. Customer comments keep their author, product, reviewer fields and date as they are (an admin
 * moderates them, does not rewrite who wrote them); editorial reviews take every field from the form.
 */
export function formToToken(form: ReviewFormValues, original: ReviewToken | null, editorial: boolean): ReviewToken {
	const moderated: ReviewToken = {
		...(original ?? {}),
		id: form.id,
		rating: form.rating,
		tl: form.tl.trim() || null,
		review: form.review,
		st: form.st as ReviewStatus,
		iv: form.iv,
		isFeatured: form.isFeatured,
		si: form.si
	};
	if (!editorial) return moderated;
	return {
		...moderated,
		to_id: form.to_id,
		from_id: form.from_id,
		con_cat_id: form.con_cat_id,
		reviewer: form.reviewer.trim() || null,
		source: form.source.trim() || null,
		review_date: form.review_date
	};
}
